import React, { useState, useEffect, useRef } from 'react';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip, Typography, TextField, Box, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, useMediaQuery } from '@mui/material';
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { NumberInputCell } from './NumberInputCell';
import { TextInputCell } from './TextInputCell';
import { HEADER_FOOTER_HEIGHT, ROW_HEIGHT } from '../utils/constants';
import { AmenityIncomeSuggested, AmenityIncomeBasic } from '../utils/newModelConstants';
import { generateRowId, CommitGuard } from '../utils/rowAddGuard';

interface AmenityIncome {
  id: string;
  name: string;
  start_month: number;
  utilization: number;
  unit_count: number;
  monthly_fee: number;
}



const columnHeaderTooltips = {
  "Name": "The name of the amenity",
  "Start Month": "When the amenity income begins (month 0 and month 1 both start at month 1)",
  "Utilization": "The equivalent of occupancy for the amenity being underwritten.",
  "Usage": "Total available units or spaces of the amenity x the utilization = Usage",
  "Unit Count": "The number of units or spaces available at the Property. For example, if there are 6 parking spaces, inputting a 6 here would reflect that.",
  "Monthly Fee": "The fee per month per “unit” or space of the amenity.",
  "Delete": "Delete the amenity",
  "Monthly": "The monthly income from the amenity",
  "Annual": "The annual income from the amenity"
}


const AmenityIncomeTable: React.FC<{
  amenityIncome: AmenityIncome[];
  setAmenityIncome: React.Dispatch<React.SetStateAction<AmenityIncome[]>>;
  /** The model's current unit count. New amenity rows start here rather than
   *  at zero, and any row that disagrees with it is flagged. Optional so the
   *  component still works standalone. */
  modelUnitCount?: number;
}> = ({ amenityIncome, setAmenityIncome, modelUnitCount = 0 }) => {
  const [rows, setRows] = useState<AmenityIncome[]>([]);
  useEffect(() => {
    setRows(getRows(amenityIncome));
  }, [amenityIncome]);

  const [editingNameRowId, setEditingNameRowId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState<string>('');
  const newAmenityInputRef = useRef<HTMLInputElement | null>(null);
  // Guards rapid duplicate adds from Autocomplete onChange + button onClick on the same user gesture.
  const commitGuardRef = useRef(new CommitGuard());
  const suggestedOptions = React.useMemo(() => {
    const existing = new Set(amenityIncome.map(a => (a.name || '').trim().toLowerCase()));
    const merged: string[] = [];
    const seen = new Set<string>();
    const source = [...AmenityIncomeSuggested, ...AmenityIncomeBasic];
    for (const item of source) {
      const name = (item?.name || '').trim();
      const key = name.toLowerCase();
      if (name && !seen.has(key)) {
        seen.add(key);
        merged.push(name);
      }
    }
    return merged.filter(n => !existing.has(n.trim().toLowerCase()));
  }, [amenityIncome]);

  const withHeaderTooltip = (header: keyof typeof columnHeaderTooltips | string, def: Partial<GridColDef> = {}): GridColDef => ({
    headerName: header,
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={(columnHeaderTooltips as any)[header] || header} arrow>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary' }}>
            {header}
          </Typography>
        </Tooltip>
        {header === 'Name' && (
          <Tooltip title="Add Amenity" arrow>
            <IconButton size="small" onClick={addRow} sx={{ p: 0.25 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    ...def,
  } as GridColDef);

  const columns: GridColDef[] = [
    withHeaderTooltip('Name', { field: 'name', editable: false, flex: 1.2,
      renderCell: (params) => (
        editingNameRowId === String(params.id) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <TextField
              value={editingNameValue}
              onChange={(e) => setEditingNameValue(e.target.value)}
              variant="standard"
              size="small"
              autoFocus
              fullWidth
              onBlur={() => {
                const trimmed = editingNameValue.trim();
                setAmenityIncome(prev => prev.map(r => r.id === String(params.id) ? { ...r, name: trimmed } : r));
                setEditingNameRowId(null);
              }}
              onKeyDown={(e) => {
                // Prevent DataGrid from hijacking typing/navigation (space, arrows, etc.)
                e.stopPropagation();
                if (e.key === 'Enter') {
                  const trimmed = editingNameValue.trim();
                  setAmenityIncome(prev => prev.map(r => r.id === String(params.id) ? { ...r, name: trimmed } : r));
                  setEditingNameRowId(null);
                } else if (e.key === 'Escape') {
                  setEditingNameRowId(null);
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              sx={{
    
                width: '100%',
                '& .MuiInputBase-input': { padding: 0, textAlign: 'left', height: '100%' }
              }}
              InputProps={{
                disableUnderline: true,
                sx: { px: 0, py: 0, height: 36, display: 'flex', alignItems: 'center', background: 'transparent' }
              }}
              inputProps={{ maxLength: 100 }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
            <span style={{ color: '#222' }}>{params.value || <span style={{ color: '#aaa' }}>Untitled</span>}</span>
            <span className="u-row-action">
              <Tooltip title="Edit name" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingNameRowId(String(params.id));
                    setEditingNameValue(String(params.value || ''));
                  }}
                  sx={{ p: 0.25 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          </Box>
        )
      )
    }),
    withHeaderTooltip('Start Month', { field: 'start_month', editable: false, type: 'number', flex: 1,
      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="start_month" 
        />
      )
    }),
    withHeaderTooltip('Unit Count', { field: 'unit_count', editable: false, type: 'number', flex: 1,
      renderCell: (params) => {
        // Flag a count that disagrees with the model. Defaulting alone does not
        // catch this: the reported bug was a hand-typed 379 against a model
        // holding 375, and seeded rows arrive at 0 before any units exist.
        const mismatch = modelUnitCount > 0 && Number(params.value) !== modelUnitCount;
        const cell = (
          <NumberInputCell
            params={params}
            handleCellChange={handleCellChange}
            field="unit_count"
          />
        );
        if (!mismatch) return cell;
        return (
          <Tooltip
            arrow
            placement="top"
            title={`This model has ${modelUnitCount.toLocaleString()} units. Click to use that, or keep your own number.`}
          >
            <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
              <Box
                component="span"
                role="button"
                aria-label={`Set unit count to ${modelUnitCount}`}
                onClick={() => handleCellChange(String(params.id), 'unit_count', modelUnitCount)}
                sx={{
                  position: 'absolute', left: 2, zIndex: 1, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, lineHeight: 1,
                  color: 'warning.main', px: 0.5, py: 0.25, borderRadius: 0.5,
                  border: '1px solid', borderColor: 'warning.main',
                  '&:hover': { bgcolor: 'warning.main', color: '#fff' },
                }}
              >
                {modelUnitCount.toLocaleString()}
              </Box>
              {cell}
            </Box>
          </Tooltip>
        );
      }
    }),
    withHeaderTooltip('Utilization', { field: 'utilization', headerName: 'Utilization %', editable: false, type: 'number', flex: 1,
      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="utilization" 
          suffix="%"
          max={100}
        />
      )
    }),
    withHeaderTooltip('Usage', {
      field: 'usage',
      headerName: 'Usage',
      flex: 1,
     
      editable: false,
      type: 'number',
      cellClassName: 'u-noneditable-cell',
      renderCell: (params) => (
        <span className="u-muted">{params.value} Units</span>
      ),
      headerClassName: 'non-editable-header'
    }),
    withHeaderTooltip('Monthly Fee', { field: 'monthly_fee', editable: false, type: 'number', flex: 1,
      renderCell: (params) => (
        <NumberInputCell 
          params={params} 
          handleCellChange={handleCellChange} 
          field="monthly_fee" 
          prefix="$"
        />
      )
    }),
    withHeaderTooltip('Monthly', {
      field: 'monthly',
      headerName: 'Monthly',
      flex: 1,
     
      editable: false,
      type: 'number',
      cellClassName: 'u-noneditable-cell',
      renderCell: (params) => {
        const raw = params.value;
        const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(/,/g, '').trim()) || 0;
        return (
          <span className="u-muted">
            ${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
      headerClassName: 'non-editable-header'
    }),
    withHeaderTooltip('Annual', {
      field: 'annual',
      headerName: 'Annual',
      flex: 1,
     
      editable: false,
      type: 'number',
      cellClassName: 'u-noneditable-cell',
      renderCell: (params) => {
        const raw = params.value;
        const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(/,/g, '').trim()) || 0;
        return (
          <span className="u-muted">
            ${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
      headerClassName: 'non-editable-header'
    }),
    {
      field: 'delete',
      headerName: '',
      flex: 0.4,
      sortable: false,
      minWidth: 50,
      renderHeader: () => <span />,
      renderCell: (params) => (
        <span className="u-row-action">
          <Tooltip title="Delete">
            <span>
              <IconButton
                onClick={() => deleteRow(params.id)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ),
    },
  ];

  const handleCellChange = (id: string, field: string, value: string | number) => {

      
    setAmenityIncome(prev => 
      prev.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };


  const processRowUpdate = (newRow: AmenityIncome) => {
    if (newRow.utilization > 100) {
      newRow.utilization = 100;
    }
    if (newRow.utilization < 0) {
      newRow.utilization = 0;
    }
    setAmenityIncome((prev) =>
      prev.map((row) => (row.id === newRow.id ? newRow : row))
    );
    return newRow;
  };

  const addRow = () => {
    const newRow: AmenityIncome = {
      id: generateRowId(),
      name: '',
      start_month: 0,
      utilization: 0,
      // An amenity almost always applies across the whole property, so the
      // model's unit count is the right starting point. Still editable.
      unit_count: modelUnitCount,
      monthly_fee: 0,
    };
    setAmenityIncome((prev) => [...prev, newRow]);
  };
  const addRowWithName = (name: string) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    // Idempotency guard against rapid same-name re-fires.
    if (commitGuardRef.current.shouldSkip(trimmed)) return;
    const newRow: AmenityIncome = {
      id: generateRowId(),
      name: trimmed,
      start_month: 0,
      utilization: 0,
      unit_count: modelUnitCount,
      monthly_fee: 0,
    };
    setAmenityIncome((prev) => [...prev, newRow]);
  };

  const deleteRow = (id: GridRowId) => {
    try {
      if (typeof window !== 'undefined' && (document as any)?.activeElement) {
        (document as any).activeElement?.blur?.();
      }
    } catch {}
    requestAnimationFrame(() => {
    setAmenityIncome((prev) => prev.filter((row) => row.id !== id));
    });
  };

  const getRows = (amenityIncome: AmenityIncome[]) => {
    return amenityIncome.map((row) => {
      const usage = Math.round(row.utilization / 100 * row.unit_count);
      const monthly = usage * row.monthly_fee;
      const annual = monthly * 12;
      return {
        ...row,
        usage,
        monthly,
        annual,
      };
    });
  };

  const CustomFooter = () => {
    // Calculate total monthly and annual income from rows
    // Use getRows to ensure 'monthly' and 'annual' are present on each row
    const computedRows = getRows(amenityIncome);
    const totalMonthlyIncome = computedRows.reduce((sum, row) => sum + (row.monthly || 0), 0);
    const totalAnnualIncome = computedRows.reduce((sum, row) => sum + (row.annual || 0), 0);

    return (
      <div style={{ 
        padding: '16px 16px',
        backgroundColor: 'transparent',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 15
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Autocomplete
            freeSolo
            size="small"
            options={suggestedOptions}
            // Always-null controlled value: prevents MUI from retaining a
            // previously-committed value that could re-fire onChange on blur.
            value={null}
            clearOnBlur
            // keep input uncontrolled to avoid focus loss on rerenders
            onChange={(_e, v) => {
              const name = typeof v === 'string' ? v : (v as any) || '';
              const trimmed = String(name).trim();
              if (trimmed) {
                addRowWithName(trimmed);
                if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add or select amenity"
                placeholder="e.g., Storage"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const currentVal = (e.target as HTMLInputElement)?.value ?? '';
                    const trimmed = currentVal.trim();
                    if (trimmed) {
                      e.preventDefault();
                      addRowWithName(trimmed);
                      if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
                    }
                  }
                }}
                inputRef={newAmenityInputRef}
                sx={{ minWidth: 220 }}
              />
            )}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const currentVal = newAmenityInputRef.current?.value ?? '';
              const trimmed = currentVal.trim();
              if (trimmed) {
                addRowWithName(trimmed);
                if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
              } else {
                addRow();
              }
            }}
            sx={{ whiteSpace: 'nowrap', minWidth: 180 }}
          >
            Add Amenity
          </Button>
        </div>
        <div style={{ display: 'flex', gap: '24px', justifyContent: 'flex-end', width: '100%' }}>
          <div style={{ textAlign: 'right' }}>
            <strong>Total Monthly Income:</strong> ${totalMonthlyIncome.toLocaleString()}
          </div>
          <div style={{ textAlign: 'right' }}>
            <strong>Total Annual Income:</strong> ${totalAnnualIncome.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  const getRowClassName = (params: any) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even';

  // --- Mobile (card) view -------------------------------------------------
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [editId, setEditId] = useState<string | null>(null);
  const editingRow = editId ? amenityIncome.find((r) => r.id === editId) ?? null : null;

  const renderField = (label: string, value: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#6b7280', fontSize: '0.62rem', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  if (isMobile) {
    const computedRows = getRows(amenityIncome);
    const totalMonthlyIncome = computedRows.reduce((sum, row) => sum + (row.monthly || 0), 0);
    const totalAnnualIncome = computedRows.reduce((sum, row) => sum + (row.annual || 0), 0);

    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {computedRows.map((row) => (
            <Box key={row.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, color: '#1f2937', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.name || 'Untitled amenity'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => setEditId(row.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton size="small" color="error" onClick={() => deleteRow(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
                {renderField('Start Month', row.start_month)}
                {renderField('Unit Count', row.unit_count)}
                {renderField('Utilization', `${row.utilization}%`)}
                {renderField('Monthly Fee', `$${Number(row.monthly_fee || 0).toLocaleString()}`)}
                {renderField('Usage', `${row.usage} units`)}
                {renderField('Monthly', `$${Number(row.monthly || 0).toLocaleString()}`)}
                {renderField('Annual', `$${Number(row.annual || 0).toLocaleString()}`)}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Add amenity + totals */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          <Autocomplete
            freeSolo
            size="small"
            fullWidth
            options={suggestedOptions}
            value={null}
            clearOnBlur
            onChange={(_e, v) => {
              const name = typeof v === 'string' ? v : (v as any) || '';
              const trimmed = String(name).trim();
              if (trimmed) {
                addRowWithName(trimmed);
                if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add or select amenity"
                placeholder="e.g., Storage"
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const currentVal = (e.target as HTMLInputElement)?.value ?? '';
                    const trimmed = currentVal.trim();
                    if (trimmed) {
                      e.preventDefault();
                      addRowWithName(trimmed);
                      if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
                    }
                  }
                }}
                inputRef={newAmenityInputRef}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={() => {
              const currentVal = newAmenityInputRef.current?.value ?? '';
              const trimmed = currentVal.trim();
              if (trimmed) {
                addRowWithName(trimmed);
                if (newAmenityInputRef.current) newAmenityInputRef.current.value = '';
              } else {
                addRow();
              }
            }}
            fullWidth
          >
            Add Amenity
          </Button>
          <Box sx={{ textAlign: 'right', fontSize: 13, color: '#1f2937' }}>
            <div><span style={{ fontWeight: 600 }}>Total Monthly Income:</span> ${totalMonthlyIncome.toLocaleString()}</div>
            <div><span style={{ fontWeight: 600 }}>Total Annual Income:</span> ${totalAnnualIncome.toLocaleString()}</div>
          </Box>
        </Box>

        {/* Edit dialog */}
        <Dialog open={!!editingRow} onClose={() => setEditId(null)} fullWidth maxWidth="xs">
          <DialogTitle>Edit {editingRow?.name || 'Amenity'}</DialogTitle>
          <DialogContent>
            {editingRow && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="Name" size="small" fullWidth value={editingRow.name ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'name', e.target.value)} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MobileNumberField label="Start Month" type="number" size="small" fullWidth value={editingRow.start_month ?? ''}
                    onChange={(e) => handleCellChange(editingRow.id, 'start_month', Number(e.target.value))} />
                  <MobileNumberField label="Unit Count" type="number" size="small" fullWidth value={editingRow.unit_count ?? ''}
                    onChange={(e) => handleCellChange(editingRow.id, 'unit_count', Number(e.target.value))}
                    helperText={
                      modelUnitCount > 0 && Number(editingRow.unit_count) !== modelUnitCount
                        ? `Model has ${modelUnitCount.toLocaleString()} units`
                        : undefined
                    } />
                </Box>
                <MobileNumberField label="Utilization" type="number" size="small" fullWidth value={editingRow.utilization ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'utilization', Math.max(0, Math.min(100, Number(e.target.value))))}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                <MobileNumberField label="Monthly Fee" type="number" size="small" fullWidth value={editingRow.monthly_fee ?? ''}
                  onChange={(e) => handleCellChange(editingRow.id, 'monthly_fee', Number(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditId(null)} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <div>
        {/* <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={addRow}
          sx={{ minWidth: 'auto', px: 2, py: 0.5 }}
        >
          Add Amenity
        </Button>
      </div> */}

      <DataGrid
        rows={rows}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnSorting
        sortingOrder={[]}
        processRowUpdate={processRowUpdate}
        slots={{ footer: CustomFooter }}
        disableRowSelectionOnClick
        rowHeight={52}
        getRowClassName={getRowClassName}
        hideFooterSelectedRowCount
        sx={{
          minWidth: "1000px",
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          '& .MuiDataGrid-main': { background: '#ffffff' },
          '& .MuiDataGrid-columnHeaders': { background: '#ffffff', minHeight: 52, maxHeight: 52, borderBottom: '1px solid #e5e7eb' },
          '& .MuiDataGrid-columnHeader': { background: '#ffffff', minHeight: 52, maxHeight: 52 },
          '& .MuiDataGrid-columnHeaderTitleContainer': { background: '#ffffff' },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: 14.5,
            fontFamily: 'inherit',
            textTransform: 'none',
            lineHeight: '52px',
            color: '#1f2937',
          },
          '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(15, 23, 42, 0.08)', background: '#ffffff', fontSize: 14.5, color: '#1f2937' },
          '& .MuiDataGrid-cell.u-noneditable-cell': { color: '#9aa3b2' },
          '& .u-editable-input': { border: 'none', borderBottom: '1px solid transparent', borderRadius: 0, background: 'transparent', fontWeight: 600, color: '#111827' },
          '& .MuiDataGrid-row:hover .u-editable-input, & .u-editable-input:focus': { borderBottom: '2px solid #4f8bd6 !important' },
          '& .MuiDataGrid-row': { background: '#ffffff' },
          '& .u-row-odd': { background: '#ffffff' },
          '& .u-row-even': { background: '#f9fafb' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#f3f6fb' },
          '& .u-row-action': {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease'
          },
          '& .MuiDataGrid-row:hover .u-row-action, & .MuiDataGrid-cell:focus-within .u-row-action': {
            opacity: 1,
            visibility: 'visible',
            pointerEvents: 'auto'
          },
          '& .MuiDataGrid-virtualScroller': { background: '#ffffff' },
          '& .MuiDataGrid-footerContainer': { background: '#ffffff', borderTop: '1px solid #e5e7eb' },
          '& .u-muted': { color: '#9aa3b2', fontWeight: 400 },
        }}
      />
    </div>
  );
};

export default AmenityIncomeTable;
